<template>
  <v-tabs
    v-if="productDetailsFiltered.length"
    :id="`product-details-tabs-${product.id}`"
    color="transparent"
    class="mt-4"
    mandatory
  >
    <v-tab
      v-for="detail in productDetailsFiltered"
      :key="detail.component"
      :href="`#tab-${detail.component}`"
      class="mr-1"
    >
      <div>
        {{ detail.title }}
      </div>
    </v-tab>

    <v-tabs-slider class="d-none" />

    <v-tabs-items touchless>
      <!-- Descrição -->
      <v-tab-item
        key="description"
        value="tab-description"
      >
        <div
          :id="`product-description-${product.id}`"
          :class="[ wholeDescription ? 'description' : 'description__hide' ]"
        >
          <div
            v-show="!wholeDescription && hasReachTextLimit"
            class="description__overlay"
          />
          {{ product.descricao }}
        </div>
        <span
          v-if="hasReachTextLimit"
          class="description__see-more mt-2 mb-4"
          @click="wholeDescription = !wholeDescription"
        >
          {{ wholeDescription ? 'ver menos' : 'ver mais' }}
          <v-icon small>
            {{ wholeDescription ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
          </v-icon>
        </span>
      </v-tab-item>

      <!-- Ficha técnica -->
      <v-tab-item
        key="specification"
        value="tab-specification"
      >
        <v-data-table
          :headers="specificationsHeader"
          :items="getSkuDatasheets"
          class="specifications-datatable mt-3 mb-4"
          hide-headers
          hide-actions
          no-data-text="Sem informações"
        >
          <template #items="{ item }">
            <tr>
              <td class="pa-2 specification">
                {{ item.label }}
              </td>
              <td
                class="pa-2 specification"
                style="font-weight: 400;"
              >
                {{ item.value }}
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-tab-item>

      <!-- Estoque do produto -->
      <v-tab-item
        key="stock"
        value="tab-stock"
        class="pt-3"
      >
        <v-radio-group
          v-if="!loadingStocks"
          v-model="storeSelected"
          @change="() => storeSelected = storeSelected"
        >
          <div
            v-if="!sortedStocks.length"
            class="stock-options"
          >
            Não foram encontrados estoques do produto...
          </div>

          <v-dialog
            v-if="changeStockDialog"
            v-model="changeStockDialog"
          >
            <ActionsDialog
              title-text="Alterar estoque de venda"
              dialog-text="Você deseja alterar o estoque em que o produto será vendido?"
              first-text="Desejo alterar"
              second-text="Cancelar"
              :first-action="() => changeStock(true)"
              :second-action="() => changeStock(false)"
              :close="() => changeStock(false)"
            />
          </v-dialog>

          <v-dialog
            v-if="outOfStockDialog"
            v-model="outOfStockDialog"
          >
            <ActionsDialog
              title-text="Alterar estoque de venda"
              dialog-text="Produto sem estoque na loja"
              first-text="Voltar"
              :first-action="() => outOfStockDialog = false"
              :close="() => outOfStockDialog = false"
            />
          </v-dialog>

          <div class="stock-options">
            <div
              v-for="(stock, index) in sortedStocks"
              :key="index"
              class="stock-options__option"
              :class="storeSelected && storeSelected.mongo_id === stock.mongo_id ? 'stock-options__option__selected' : ''"
            >
              <div
                class="stock-options__option__radio"
                @click="setStoreDialog(stock)"
              >
                <v-radio
                  :value="stock"
                />
              </div>

              <div
                class="stock-options__option__name"
                @click="setStoreDialog(stock)"
              >
                {{ stock.store }}
              </div>

              <div
                class="stock-options__option__unities"
                :class="stock.stock === 0 ? 'stock-options__option__unities__zero' : ''"
              >
                {{ stock.stock }} un.
              </div>

              <div class="stock-options__option__distance">
                {{ stock.distance || '-' }}
              </div>

              <div
                class="stock-options__option__price"
                @click="toggleHidePrice(index)"
              >
                <v-icon
                  :id="`${index}-price-icon`"
                  color="#8A8691"
                  style="margin: 0 6px 2px 0;"
                >
                  mdi-eye
                </v-icon>
                <span
                  :id="`${index}-stock-price`"
                  style="display: none;"
                >
                  {{ stock.price | currency }}
                </span>
                <div
                  :id="`${index}-stock-hide-price`"
                  class="stock__hide-price"
                />
              </div>
            </div>
          </div>
        </v-radio-group>

        <Loader
          v-else
          :size="30"
          class="mt-3"
        />
      </v-tab-item>

      <!-- Garantias -->
      <v-tab-item
        key="addons"
        value="tab-addons"
      >
        <v-expansion-panel
          v-if="getSkuAddOns('GE').length"
          class="mb-2"
        >
          <v-expansion-panel-content>
            <template #header>
              Garantia estendida
            </template>
            <v-divider />
            <v-data-table
              :headers="addonsHeader"
              :items="getSkuAddOns('GE')"
              class="stock-datatable mt-1"
              style="width: 100%"
              hide-headers
              hide-actions
            >
              <template #items="{ item }">
                <tr style="border: none">
                  <td
                    class="addons px-0"
                    style="width: 40%; justify-content: flex-start;"
                  >
                    {{ item.plan }}
                  </td>
                  <td
                    class="addons px-0"
                    style="width: 65%; justify-content: flex-end;"
                  >
                    {{ item.price | currency }}
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          v-if="getSkuAddOns('SM').length"
          class="mb-2"
        >
          <v-expansion-panel-content>
            <template #header>
              Seguro mobile (12 meses)
            </template>
            <v-divider />
            <v-data-table
              v-if="getSkuAddOns('SM')"
              :headers="addonsHeader"
              :items="getSkuAddOns('SM')"
              class="stock-datatable mt-1"
              hide-headers
              hide-actions
            >
              <template #items="{ item }">
                <tr style="border: none">
                  <td
                    class="addons px-0"
                    style="width: 80%; justify-content: flex-start;"
                  >
                    {{ item.description }}
                  </td>
                  <td
                    class="addons px-0"
                    style="width: 65%; justify-content: flex-end;"
                  >
                    {{ item.price | currency }}
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          v-if="getSkuAddOns('PS').length"
          class="mb-2"
        >
          <v-expansion-panel-content>
            <template #header>
              Serviços
            </template>
            <v-divider />
            <v-data-table
              v-if="getSkuAddOns('PS')"
              :headers="addonsHeader"
              :items="getSkuAddOns('PS')"
              class="stock-datatable mt-1"
              hide-headers
              hide-actions
            >
              <template #items="{ item }">
                <tr style="border: none">
                  <td
                    class="addons px-0"
                    style="width: 80%; justify-content: flex-start;"
                  >
                    {{ item.description }}
                  </td>
                  <td
                    class="addons px-0"
                    style="width: 45%; justify-content: flex-end;"
                  >
                    {{ item.price | currency }}
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-tab-item>

      <!-- Edição do preço produto -->
      <v-tab-item
        v-if="currentPrice.price_limit_discount && currentPrice.price > currentPrice.price_limit_discount"
        key="edit"
        value="tab-edit"
      >
        <div class="d-flex justify-center">
          <v-card class="mt-2 edit">
            <v-card-text class="py-0 px-5">
              Arraste ou digite o valor do desconto que deseja aplicar.
            </v-card-text>

            <v-card-actions class="edit__content">
              <div class="py-0 ma-0 edit__slider">
                <v-slider
                  v-model="discountValue"
                  class="ma-0"
                  thumb-label="always"
                  :thumb-size="30 * shrinkPercentage"
                  track-color="#F1F1F1"
                  :step="0.01"
                  :min="0"
                  :max="getMaxDiscount"
                >
                  <template #thumb-label>
                    <span class="edit__percentage-thumb">
                      {{ discountValue | currency }}
                    </span>
                  </template>
                </v-slider>
                <div class="edit__slider__values">
                  <span>{{ currentPrice.price | currency }}</span>
                  <span>{{ currentPrice.price_limit_discount | currency }}</span>
                </div>
              </div>

              <money
                v-model="discountValue"
                v-bind="money"
                class="edit__input"
              />
            </v-card-actions>

            <div class="edit__slider__notification">
              {{ discountValue === getMaxDiscount ? '* Valor alterado para o desconto máximo permitido' : '' }}
            </div>

            <v-card-text
              class="pa-0 edit__result"
            >
              <span>
                Preço atualizado:
              </span>
              <span class="edit__final-price">
                {{ getFinalPrice | currency }} ( {{ getDiscountPercentage }} % de desconto)
              </span>
            </v-card-text>

            <v-card-actions class="pa-0 ma-0">
              <v-btn
                dark
                color="#140C22"
                class="edit__confirm-btn"
                @click="addDiscount"
              >
                Aplicar desconto
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>
      </v-tab-item>
    </v-tabs-items>
  </v-tabs>
</template>

<script>
/* eslint-disable camelcase */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable import/no-unresolved */
/* eslint-disable import/extensions */
/* eslint-disable no-unused-expressions */
/* eslint-disable vue/no-mutating-props */

import { everything } from '@sejazipper/layer-api-client';
import { mapGetters } from 'vuex';
import Promise from 'bluebird';
import { Money } from 'v-money';
import orderBy from 'lodash.orderby';
import _ from 'lodash';
import { EventBus } from '@/event-bus.js';
import { useZipperApi } from '@/composables/useZipperApi';

const { client } = useZipperApi();
const publicIp = require('public-ip');

export default {
  components: {
    Money,
  },
  props: {
    product: {
      type: Object,
      default: () => ({}),
    },
    store: {
      type: Object,
      default: () => {},
    },
    skuSelected: {
      type: Object,
      default: () => {},
    },
    currentPrice: {
      type: Object,
      default: () => {},
    },
    shrinkPercentage: {
      type: Number,
      default: 1,
    },
  },

  data: () => ({
    stocksByStore: [],
    // temporary values, need to remove this later
    specifications: [
      { specification: 'Capacidade Reservatório', value: '1,6 Litros' },
      { specification: 'Colher Dosadora', value: 'Sim' },
      { specification: 'Desligamento automático', value: 'Não' },
      { specification: 'Porta Filtro Removível', value: 'Sim' },
      { specification: 'Marca', value: 'Philco' },
      { specification: 'Placa de Aquecimento', value: 'Sim' },
    ],
    productDetails: [
      { title: 'Descrição', component: 'description' },
      { title: 'Ficha técnica', component: 'specification' },
      { title: 'Estoque', component: 'stock' },
      { title: 'Serviços', component: 'addons' },
      { title: 'Editar produto', component: 'edit' },
    ],
    specificationsHeader: [
      { value: 'label', align: 'left' },
      { value: 'value', align: 'left' },
    ],
    stocksHeader: [
      { value: 'store', align: 'left', sortable: false },
      { value: 'stock', align: 'left', sortable: false },
      { value: 'distance', align: 'left', sortable: false },
      { value: 'price', align: 'left', sortable: false },
    ],
    addonsHeader: [
      { value: 'plan', align: 'left', sortable: false },
      { value: 'price', align: 'left', sortable: false },
    ],
    wholeDescription: false,
    hasReachTextLimit: false,
    loadingStocks: false,
    discountValue: 0,
    approvalRequestDialog: false,
    statusRequest: null,
    approvalRequest: null,
    money: {
      decimal: ',',
      thousands: '.',
      prefix: 'R$ ',
      suffix: '',
      precision: 2,
      masked: false,
    },
    storeSelected: null,
    storeToChangeStock: false,
    changeStockDialog: false,
    outOfStockDialog: false,
  }),

  computed: {
    ...mapGetters(['getStore', 'getCustomer', 'getProfile']),

    sortedStocks() {
      const stockOrder = (stock) => (stock.distance ? parseFloat(stock.distance.replace(/\D/, '')) : null);
      const sortedStocks = orderBy(this.stocksByStore, [stockOrder], ['asc']);
      const [sellerStore] = _.remove(sortedStocks, (stock) => stock.store === this.getStore.name);
      const [eCommerce] = _.remove(sortedStocks, (stock) => stock.store.match(/e-commerce/gi));

      if (eCommerce) sortedStocks.unshift(eCommerce);
      if (sellerStore) sortedStocks.unshift(sellerStore);

      return sortedStocks;
    },

    productDetailsFiltered() {
      return this.productDetails.filter((detail) => (
        this.hasDetail(detail.component)
      ));
    },

    eCommerceProduct() {
      const eCommerce = this.sortedStocks.find((s) => s.store.match(/e-commerce/gi)) || {};

      // temporary if 'cause Angeloni still doesn't have e-commerce store
      if (!eCommerce.price) eCommerce.price = this.getStoreSkuPrice('regular') * 0.5;

      return eCommerce;
    },

    getMaxDiscount() {
      const skuPrice = this.currentPrice;
      const discount = (skuPrice.price - skuPrice.price_limit_discount);
      return discount;
    },

    getFinalPrice() {
      const skuPrice = this.currentPrice;
      const price = skuPrice.price - this.discountValue;

      return price;
    },

    getDiscountPercentage() {
      const skuPrice = this.currentPrice;
      const price = (((skuPrice.price - this.getFinalPrice) / skuPrice.price) * 100).toFixed(2);

      return price;
    },

    getHighestPrice() {
      let maxSkuPrice = {};
      let maxPrice = -Infinity;

      this.product.skus.forEach((sku) => {
        if (sku.sku_prices && sku.sku_prices.length > 0) {
          sku.sku_prices.forEach((sku_price) => {
            const { price } = sku_price;

            if (price && price > maxPrice) {
              maxPrice = price;
              maxSkuPrice = sku;
            }
          });
        }
      });

      return maxSkuPrice;
    },

    getSkuDatasheets() {
      // @TODO => saber qual sku está selecionado para retornar a ficha técnica dele
      return this.getHighestPrice.sku_datasheets.map((skuDatasheet) => ({
        label: skuDatasheet.label,
        value: skuDatasheet.value,
      }));
    },
  },

  watch: {
    product() {
      this.loadProductDetails();
    },
  },

  mounted() {
    this.loadProductDetails();
  },

  methods: {
    loadProductDetails() {
      this.getStoreDistancesBySKUS(this.product);

      const productDescription = document.getElementById(`product-description-${this.product.id}`);
      const root = document.querySelector(':root');
      const tabs = document.querySelector(`#product-details-tabs-${this.product.id}`);
      const isHTML = this.product.descricao.includes('html');
      const descriptionMaxHeight = (isHTML ? 200 : 80) * this.shrinkPercentage;

      if (isHTML) productDescription.innerHTML = this.product.descricao;
      tabs.style.setProperty('--shrink-percentage', this.shrinkPercentage);
      root.style.setProperty('--description-max-height', `${descriptionMaxHeight}px`);

      this.hasReachTextLimit = productDescription.clientHeight >= (80 * this.shrinkPercentage);
    },

    getSkuAddOns(addonType) {
      let skuAddOnsWithRecommended = this.skuSelected.sku_add_ons.filter((skuAddOn) => skuAddOn.type === addonType && skuAddOn.store_id === this.product.store_id).map((skuAddOn) => ({
        plan: `${skuAddOn.plan} ${skuAddOn.plan > 1 ? 'meses' : 'mês'}`,
        price: skuAddOn.price_plan,
        description: skuAddOn.description.charAt(0).toUpperCase() + skuAddOn.description.slice(1).toLowerCase(),
        installments: `em até 10x de ${(skuAddOn.price_plan / 10).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
      }));

      if (skuAddOnsWithRecommended.length) {
        const skuAddOnWithMaxPlan = skuAddOnsWithRecommended.reduce((prev, current) => ((prev.price > current.price) ? prev : current));

        skuAddOnsWithRecommended = skuAddOnsWithRecommended.map((skuAddOn) => ({
          ...skuAddOn,
          recommended: skuAddOn.price === skuAddOnWithMaxPlan.price,
        }));

        return orderBy(skuAddOnsWithRecommended, 'price');
      }

      return [];
    },

    emitSnackbar(text) {
      const snackBar = {};
      snackBar.value = true;
      snackBar.text = text;
      snackBar.color = '#140C22';
      snackBar.button = false;
      snackBar.icon = '$vuetify.icons.checkIcon';
      snackBar.class = {
        height: '120px',
        'font-size': '16px',
        'font-weight': '600',
        'justify-content': 'center',
      };
      EventBus.$emit('activeSnackBar', snackBar);
    },

    hasDetail(detail) {
      const detailsVerify = {
        description: !!this.product.descricao,
        specification: !!this.specifications.length,
        stock: !!this.product.skus.length,
        addons: !!this.product.skus.length,
        edit: !!this.getCustomer.salesAssistant && this.currentPrice.price_limit_discount && this.currentPrice.price > this.currentPrice.price_limit_discount,
      };

      return detailsVerify[detail];
    },

    async getAllProductSkusById(id) {
      const { skus } = await client.chain.query
        .findOMSProductById({ productId: id })
        .get({ skus: { ...everything } });

      return skus;
    },

    async getStoreDistancesBySKUS(product) {
      this.loadingStocks = true;
      this.stocksByStore = [];

      const userIp = await publicIp.v4();

      const [
        allProductSkus,
        storesDistances,
      ] = await Promise.all([
        this.getAllProductSkusById(product.id),
        client.chain.query
          .getStoreDistancesByCustomerId({
            customerId: this.getCustomer.id,
            ip: userIp,
          })
          .get({ ...everything, address: { ...everything } }),
      ]);

      const stocksByStoreId = allProductSkus[0].sku_stocks;

      stocksByStoreId.forEach((stock) => {
        const storeDistance = storesDistances.find((store) => store._id === stock.store.mongo_id);
        const { price } = allProductSkus[0].sku_prices.find((pr) => pr.store_id === stock.store.id);

        this.stocksByStore.push({
          mongo_id: stock.store.mongo_id,
          store: stock.store.name,
          stock: stock.stock,
          distance: storeDistance ? storeDistance.distance : '',
          price,
        });
      });

      if (this.sortedStocks.length) {
        this.storeSelected = this.sortedStocks.find((item) => item.mongo_id === (this.product.isCD ? '52' : this.getStore.id));
        this.storeToChangeStock = this.storeSelected;
      }
      this.loadingStocks = false;
    },

    toggleHidePrice(stockIndex) {
      const price = document.getElementById(`${stockIndex}-stock-price`);
      const hidePrice = document.getElementById(`${stockIndex}-stock-hide-price`);
      const priceEyeIcon = document.getElementById(`${stockIndex}-price-icon`);
      const priceIsVisible = price.style.display === 'block';

      priceEyeIcon.classList.remove(`mdi-eye${priceIsVisible ? '-off' : ''}`);
      priceEyeIcon.classList.add(`mdi-eye${priceIsVisible ? '' : '-off'}`);
      hidePrice.style.display = priceIsVisible ? 'block' : 'none';
      price.style.display = priceIsVisible ? 'none' : 'block';
    },

    getStoreSkuPrice(priceType) {
      const { old_price, price } = this.getHighestPrice.sku_prices[0];

      const prices = {
        old: (old_price > price) ? old_price : null,
        regular: price,
      };

      return prices[priceType];
    },

    async addDiscount() {
      this.emitSnackbar('Valor do produto alterado com sucesso');
      EventBus.$emit('addDiscount', this.getFinalPrice);
    },

    toggleStockDialog(status) {
      this.changeStockDialog = status;
    },

    setStoreDialog(item) {
      if (item.stock > 0) {
        this.storeToChangeStock = item;
        this.toggleStockDialog(true);
      } else {
        this.outOfStockDialog = true;
      }
    },

    changeStock(status) {
      if (status) {
        this.storeToChangeStock.mongo_id === '52'
          ? this.product.isCD = true
          : this.product.isCD = false;
        this.storeSelected = this.storeToChangeStock;

        if (this.product.isCD) {
          this.$emit('change-quantity', 1);
        }

        // @TODO => ativar para v1
        // const stockSelected = this.skuSelected.sku_stocks.find((stock) => stock.store.name === this.storeToChangeStock.store);
        // if (stockSelected) {
        //   this.storeSelected = this.storeToChangeStock;
        //   // eslint-disable-next-line vue/no-mutating-props
        //   this.product.isCD = stockSelected.store.id;
        //   this.$emit('change-quantity', stockSelected.stock > 0 ? 1 : 0);
        // }
      }

      this.toggleStockDialog(false);
    },
  },
};
</script>

<style scoped lang="scss">
.description {
  margin-top: calc(12px * var(--shrink-percentage));
  overflow: hidden;
  font-family: 'Roboto';
  font-size: calc(12px * var(--shrink-percentage));
  line-height: calc(16px * var(--shrink-percentage));
  color: #140C22;
  text-align: start;
  &__hide {
    margin-top: calc(12px * var(--shrink-percentage));
    overflow: hidden;
    font-family: 'Roboto';
    font-size: calc(12px * var(--shrink-percentage));
    line-height: calc(16px * var(--shrink-percentage));
    color: #140C22;
    text-align: start;
    max-height: var(--description-max-height);
  }
  &__see-more {
    display: block;
    text-align: start;
    font-size: calc(12px * var(--shrink-percentage));
    line-height: calc(14px * var(--shrink-percentage));
    letter-spacing: 0.01em;
    color: rgba(20, 12, 34, 0.5);
  }
  &__overlay {
    z-index: 10;
    position: absolute;
    width: 100%;
    height: var(--description-max-height);
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 88.24%, #FDFCFB 100%);
  }
}

.specification {
  height: calc(34px * var(--shrink-percentage));
  font-family: 'Roboto';
  font-weight: 600;
  font-size: calc(12px * var(--shrink-percentage));
  line-height: calc(14px * var(--shrink-percentage));
  letter-spacing: 0.01em;
  color: #8A8691;
  text-align: start;
  width: 50%;
}

.stock-options {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;

  &__option {
    display: flex;
    width: 100%;
    height: 48px;
    padding: 12px;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    text-align: center;
    border-radius: 7px;
    border: 1px solid #EEE;
    background: #FFF;
    box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.05);

    color: var(--brand-extendidas-soft-black, #8A8691);
    font-family: Nunito Sans;
    font-size: 12px;
    font-style: normal;

    &__selected {
      border: 1.5px solid #140C22;
      box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.25);
    }

    &__radio {
      width: 10%;
    }

    &__name {
      width: 35%;
      font-weight: 600;
      text-align: left;
    }

    &__unities {
      width: 15%;
      font-weight: 600;

      &__zero {
        color: #FF5146;
      }
    }

    &__distance {
      width: 15%;
      font-weight: 400;
      text-align: center;
    }

    &__price {
      width: 25%;
      display: flex;
      align-items: center;
    }

    @media (max-width: 450px) {
      padding: 8px;
      font-size: 10px;
    }

    @media (max-width: 380px) {
      padding: 6px;
      font-size: 9px;
    }
  }
}

::v-deep .v-dialog {
  display: flex;
  justify-content: center;
  box-shadow: none !important;
}

::v-deep .stock-options__option__price > .v-icon {
  font-size: 20px !important;
  @media (max-width: 400px) {
    font-size: 16px !important;
  }
}

::v-deep .v-input--selection-controls__ripple {
  position: relative;
  width: auto
}

::v-deep .v-input--selection-controls {
  margin-top: 0px;
  padding-top: 0px;
  width: 100%;
}

::v-deep .v-input--radio-group--column .v-radio:not(:last-child):not(:only-child) {
  margin-bottom: 0px;
}

::v-deep .v-radio {
  margin-right: 0px;
  margin-left: -10px;
}

::v-deep .v-input--selection-controls .v-input__control {
  width: inherit;
}

::v-deep .v-input--selection-controls__input input {
  pointer-events: none;
}

::v-deep .accent--text {
  color: #000000 !important;
}
.stock {
  height: calc(38px * var(--shrink-percentage));
  font-weight: 600;
  font-size: calc(12px * var(--shrink-percentage));
  line-height: calc(15px * var(--shrink-percentage));
  color: #8A8691;
  width: 15%;
  display: flex;
  justify-content: center;
  align-items: center;
  &__hide-price {
    width: calc(55px * var(--shrink-percentage));
    height: calc(10px * var(--shrink-percentage));
    margin-bottom: calc(2px * var(--shrink-percentage));
    background: linear-gradient(90deg, #91868E -1.82%, #DDDDDD -1.81%, #BDBDBD 119.09%, #D1D1D1 119.09%);
    border-radius: calc(2px * var(--shrink-percentage));
  }
}

.addons {
  height: calc(20px * var(--shrink-percentage));
  font-weight: 600;
  font-size: calc(12px * var(--shrink-percentage));
  line-height: calc(15px * var(--shrink-percentage));
  color: #8A8691;
  width: 15%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.edit {
  display: flex;
  flex-flow: column wrap;
  justify-content: space-between;
  align-items: center;
  padding: calc(24px * var(--shrink-percentage)) 0px;
  max-width: calc(380px * var(--shrink-percentage));
  height: calc(360px * var(--shrink-percentage));
  background: #FFFFFF;
  border-radius: 7px;
  font-family: 'Nunito Sans';
  font-weight: 600;
  font-size: calc(16px * var(--shrink-percentage));
  line-height: calc(22px * var(--shrink-percentage));
  color: #140C22;
  &__content {
    display: flex;
    gap: 16px;
  }
  &__slider {
    display: flex;
    flex-flow: column wrap;
    width: 100%;
    padding: var(--shrink-percentage);
    &__values {
      display: flex;
      justify-content: space-between;
      width: 100%;
      font-size: calc(12px * var(--shrink-percentage));
      line-height: 102%;
      color: #000000;
    }
    &__notification {
      color: #FF5146;
      font-family: 'Roboto';
      font-style: normal;
      font-weight: 400;
      font-size: 10px;
      line-height: 12px;
      width: 100%;
      height: 12px;
      min-height: 12px;
      text-align: left;
      padding-left: 36px;
      margin-top: -20px;
    }
  }
  &__final-price {
    display: inline;
    margin-left: calc(10px * var(--shrink-percentage));
    padding: calc(8px * var(--shrink-percentage)) calc(12px * var(--shrink-percentage));
    width: fit-content;
    height: calc(32px * var(--shrink-percentage));
    background: #3D6961;
    border-radius: calc(4px * var(--shrink-percentage));
    line-height: 102%;
    color: #FFFFFF;
  }
  &__result {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  &__confirm-btn {
    font-family: 'Roboto';
    font-weight: 500;
    font-size: calc(12px * var(--shrink-percentage));
    line-height: calc(14px * var(--shrink-percentage));
    max-width: calc(141px * var(--shrink-percentage));
    height: calc(34px * var(--shrink-percentage));
    padding: calc(10px * var(--shrink-percentage)) calc(16px * var(--shrink-percentage));
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.25);
    border-radius: calc(4px * var(--shrink-percentage));
    text-transform: capitalize;
  }
  &__percentage-thumb {
    display: inline-block;
    white-space: nowrap;
    line-height: 102%;
    color: #FFFFFF;
    position: absolute;
    text-align: center;
    font-size: calc(12px * var(--shrink-percentage));
    padding: calc(5px * var(--shrink-percentage)) calc(4px * var(--shrink-percentage));
    min-width: calc(60px * var(--shrink-percentage));
    height: calc(24px * var(--shrink-percentage));
    background: #3D6961;
    border-radius: calc(4px * var(--shrink-percentage));
  }
  &__input {
    text-align: right;
    width: 100px;
    height: 50px;
    padding: 0 12px;
    border: 1px solid #EEE;
    border-radius: 8px;
    font-size: 16px;
    font-family: 'Roboto', sans-serif;
    &:focus {
      outline-color: #140C22;
    }
  }
}

.specifications-datatable {
  tr:nth-child(odd) {
    border: none !important;
    background: #F5F5F5;
  }
  tr:nth-child(even) {
    border: none !important;
    background: #FCFCFC;
  }
}

.stock-datatable tr {
  display: flex;
  border-radius: 7px;
  border: 1px solid #EEE;
  background: #FFF;
  box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.05);
  padding: 12px;
  margin-bottom: 8px;
}

::v-deep .v-tabs {
  &__item {
    font-family: 'Roboto';
    font-weight: 400;
    border-radius: calc(7px * var(--shrink-percentage));
    text-transform: none;
    height: calc(39px * var(--shrink-percentage))
  }
  &__item:not(.v-tabs__item--active) {
    padding: calc(10px * var(--shrink-percentage)) calc(6px * var(--shrink-percentage));
    font-size: calc(14px * var(--shrink-percentage));
    line-height: calc(16px * var(--shrink-percentage));
    color: #8A8691;
  }
  &__item--active {
    padding: calc(10px * var(--shrink-percentage)) calc(16px * var(--shrink-percentage));
    font-size: calc(16px * var(--shrink-percentage));
    line-height: calc(19px * var(--shrink-percentage));
    background: #F4FFBA;
    color: #140C22;
  }
}

::v-deep .v-slider {
  &__thumb-label {
    border-radius: 50px 0px 0px;
    background: #3D6961 !important;
  }
  &__track {
    height: calc(6px * var(--shrink-percentage));
    &__container {
      height: calc(6px * var(--shrink-percentage));
      border-radius: calc(10px * var(--shrink-percentage));
    }
  }
  &__track-fill {
    height: calc(6px * var(--shrink-percentage));
  }
}

::v-deep .v-messages {
  display: none;
}

::v-deep .v-input__slot {
  margin: 0;
}

::v-deep .v-expansion-panel__header {
  padding-bottom: 8px;
  color: #8a8691;
  justify-content: space-between;
}

::v-deep .theme--light.v-expansion-panel .v-expansion-panel__container .v-expansion-panel__header .v-expansion-panel__header__icon .v-icon {
  color: rgba(0,0,0,0.54);
  font-size: 18px;
}

::v-deep .v-divider {
  margin: 0 1rem 0 1rem;
}
</style>
